import React, { useContext, useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';
import { vehicleContext } from '../VehicleProvider';
import HeaderUsedWrapper from './HeaderUsed';
import HeaderNewWrapper from './HeaderNew';
import { IS_T3_WEBSITE, IS_TESTING_ENVIRONMENT } from '../../constants/environment';

import HeaderTier3Wrapper from './HeaderTier3';
import useGetDealerDetails from '../../hooks/useGetDealerDetails';
import type { Impel360ViewCTA } from '../../types';
import mockViewContent from '../../mock-content.json';
import MetaDataComponent from '../MetaDataComponent';

interface VehicleHeaderProps {
  cta360View: Impel360ViewCTA;
}
const VehicleHeader: React.FC<VehicleHeaderProps> = ({ cta360View }) => {
  const vehicleViewCta = IS_TESTING_ENVIRONMENT ? mockViewContent : cta360View;

  const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 1299 });
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const { vehicleData, loading } = useContext(vehicleContext);

  const vehicleType = vehicleData?.vehicleType;
  const isNew = vehicleType === 'new';

  const hasDealerImages = !!vehicleData?.vehicleDealerImages;

  const locationSearch = typeof window !== 'undefined' ? window.location.search : '';
  const market = new URLSearchParams(locationSearch).get('market') ?? 'ca';

  const dealer = vehicleData?.kvpsSyncId;
  const dealerPrefix = market.includes('ca') ? 'CANA' : 'USAA';
  const { dealerInfo } = useGetDealerDetails(dealer ? `${dealerPrefix}${dealer}` : null);

  const renderContent = useMemo(() => {
    if (!loading && !vehicleData) return null;
    // Tier 3 New Vehicles
    if (IS_T3_WEBSITE && isNew && hasDealerImages) {
      return (
        <HeaderTier3Wrapper
          vehicleData={vehicleData}
          cta360View={vehicleViewCta}
          isTablet={isTablet}
          isMobile={isMobile}
          loading={loading}
        />
      );
    }
    // Tier 3 and Tier 1 Used Vehicles
    if (!isNew) {
      return (
        <HeaderUsedWrapper
          vehicleData={vehicleData}
          cta360View={vehicleViewCta}
          isTablet={isTablet}
          isMobile={isMobile}
          loading={loading}
        />
      );
    }
    // Tier 1 New Vehicles
    return (
      <HeaderNewWrapper
        vehicleData={vehicleData}
        cta360View={vehicleViewCta}
        isTablet={isTablet}
        isMobile={isMobile}
      />
    );
  }, [loading, vehicleData, isNew, hasDealerImages, vehicleViewCta, isTablet, isMobile]);

  return (
    <>
      {!loading && <MetaDataComponent dealerInfo={dealerInfo} vehicleData={vehicleData} />}
      {renderContent}
    </>
  );
};

export default VehicleHeader;
